import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { USD_CURRENCY_MASK } from '@interxfi/design-system/components/input/text-input/masks';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';
import {
  ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
  DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER,
  getNumericStringValidator,
} from '../../../../../../util/validation';

export const programControlsFundingAmountLimitsSchema = z.object({
  minAmount: getNumericStringValidator('Min Amount', {
    required: ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
    gte: 0,
    displayFormatter: DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER,
  }),
  maxAmount: getNumericStringValidator('Max Amount', {
    required: ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
    gte: 0,
    displayFormatter: DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER,
  }),
});

type ProgramControlsFundingAmountLimits = z.infer<typeof programControlsFundingAmountLimitsSchema>;

const defaultProgramControlsFundingAmountLimits: ProgramControlsFundingAmountLimits = {
  minAmount: '',
  maxAmount: '',
};

export const programControlsFundingMethodWithLimitsFormSchema = z.object({
  value: z.boolean(),
  limits: programControlsFundingAmountLimitsSchema,
});

export type ProgramControlsFundingMethodWithLimitsFormValues = z.infer<typeof programControlsFundingMethodWithLimitsFormSchema>;

export const defaultProgramControlsFundingMethodWithLimitFormValues: ProgramControlsFundingMethodWithLimitsFormValues = {
  value: false,
  limits: defaultProgramControlsFundingAmountLimits,
};

interface ProgramControlsFundingMethodWithLimitsFormProps {
  lens: Lens<ProgramControlsFundingMethodWithLimitsFormValues>;
  settingLabel: string;
}

export function ProgramControlsFundingMethodWithLimitsForm({ lens, settingLabel }: ProgramControlsFundingMethodWithLimitsFormProps) {
  const { setValue } = useFormContext();
  const canUseMethod = useWatch(lens.focus('value').interop());
  const { name: limitsName } = lens.focus('limits').interop();
  const handleToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) {
        setValue(limitsName, { ...defaultProgramControlsFundingAmountLimits }, { shouldValidate: true });
      }
    },
    [limitsName, setValue],
  );

  return (
    <AccordionFormGroup.Section
      toggleInputElement={<AppSwitchInputWithLens align="space-between" label={settingLabel} lens={lens.focus('value')} onChange={handleToggle} />}
    >
      {canUseMethod ? (
        <>
          <AppTextInputWithLens label="Transaction Min" lens={lens.focus('limits.minAmount')} maskOptions={USD_CURRENCY_MASK} />
          <AppTextInputWithLens label="Transaction Max" lens={lens.focus('limits.maxAmount')} maskOptions={USD_CURRENCY_MASK} />
        </>
      ) : null}
    </AccordionFormGroup.Section>
  );
}
